import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import drawManager from '../lessonPlayer/managers/DrawManager';

const ONE_COLUMN_WIDTH = 180;
const TWO_COLUMN_WIDTH = 290;
/** Threshold: saved/legacy widths at or above this are treated as 2-column. */
const TWO_COLUMN_MIN_WIDTH = 200;
/** Icon-only (labels off) widths for 1- and 2-column layouts. */
const COLLAPSED_ONE_COLUMN_WIDTH = 65;
const COLLAPSED_TWO_COLUMN_WIDTH = 180;
const DOCK_GAP_FROM_DRAW = 10;
const DEFAULT_DRAG_BAR_HEIGHT = 36;
/** Drag distance before resize chooses a column target instead of toggling. */
const RESIZE_TOGGLE_DRAG_PX = 8;

const normalizeExpandedWidth = (w) => (
  (Number(w) >= TWO_COLUMN_MIN_WIDTH) ? TWO_COLUMN_WIDTH : ONE_COLUMN_WIDTH
);

const getModeWidth = (twoColumn, labelsCollapsed) => {
  if (labelsCollapsed) {
    return twoColumn ? COLLAPSED_TWO_COLUMN_WIDTH : COLLAPSED_ONE_COLUMN_WIDTH;
  }
  return twoColumn ? TWO_COLUMN_WIDTH : ONE_COLUMN_WIDTH;
};

// --- CHATTER FIX (search "CHATTER" to back out) ---
const PIN_ENTER_HYSTERESIS_PX = 2;
const PIN_EXIT_HYSTERESIS_PX = 2;
const STYLE_DEAD_ZONE_PX = 1;
/** Only rewrite stored x/y when clearly outside bounds (keeps bottom clamp without edge chatter). */
const BOUNDS_RECLAMP_EPS_PX = 2;
// --- end CHATTER constants ---

/** Fallback tallest 1-col labeled menu height; stage min-height uses max(this, measured). */
const TOOLBAR_MAX_HEIGHT_FALLBACK_PX = 560;
const VISIBLE_EPS_PX = 1;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
const clampPx = (value, min, max) => Math.round(clamp(value, min, max));

const getClientRect = (el) => {
  if (!el) {
    return { left: 0, top: 0, right: 0, bottom: 0 };
  }
  const { left, top, right, bottom } = el.getBoundingClientRect();
  return { left, top, right, bottom };
};

const intersectRects = (a, b) => {
  const left = Math.max(a.left, b.left);
  const top = Math.max(a.top, b.top);
  const right = Math.min(a.right, b.right);
  const bottom = Math.min(a.bottom, b.bottom);
  return {
    left,
    top,
    right: Math.max(left, right),
    bottom: Math.max(top, bottom),
  };
};

const rectHasArea = (rect, eps = VISIBLE_EPS_PX) => (
  rect.right - rect.left > eps && rect.bottom - rect.top > eps
);

/** Resolve this question's float chrome by lessonElementId (safe with multiple on screen). */
const getFloatEls = (lessonElementId) => {
  if (!lessonElementId) {
    return { stage: null, topAnchor: null, bottomAnchor: null };
  }
  return {
    stage: document.getElementById(`draw-float-stage-${lessonElementId}`),
    topAnchor: document.getElementById(`draw-float-anchor-top-${lessonElementId}`),
    bottomAnchor: document.getElementById(`draw-float-anchor-bottom-${lessonElementId}`),
  };
};

/** Visible host viewport for sticky (standalone) mode: window ∩ <main>. */
const getHostViewRect = (fromEl) => {
  const win = {
    left: 0,
    top: 0,
    right: window.innerWidth || document.documentElement.clientWidth,
    bottom: window.innerHeight || document.documentElement.clientHeight,
  };
  const main = fromEl?.closest?.('main') || document.querySelector('main');
  if (!main) {
    return win;
  }
  return intersectRects(getClientRect(main), win);
};

/**
 * Sticky clamp band: between this question's top/bottom anchors ∩ visible main.
 * Horizontal: main viewport (menu may float left/right of the draw area).
 */
const getClampBoundsRect = (lessonElementId, topAnchor) => {
  const { topAnchor: topEl, bottomAnchor } = getFloatEls(lessonElementId);
  const top = topEl || topAnchor;
  const hostView = getHostViewRect(top);
  if (!top || !bottomAnchor) {
    return hostView;
  }
  const topRect = getClientRect(top);
  const bottomRect = getClientRect(bottomAnchor);
  return {
    left: hostView.left,
    right: hostView.right,
    top: Math.max(hostView.top, topRect.top),
    bottom: Math.min(hostView.bottom, bottomRect.bottom),
  };
};

/** Stage (anchor span) intersects visible host — otherwise hide the menu. */
const isDrawStageInView = (lessonElementId) => {
  const { stage, topAnchor, bottomAnchor } = getFloatEls(lessonElementId);
  const bandEl = stage || topAnchor;
  if (!bandEl) {
    return true;
  }
  const hostView = getHostViewRect(bandEl);
  const topRect = getClientRect(topAnchor || bandEl);
  const bottomRect = getClientRect(bottomAnchor || bandEl);
  const stageBand = {
    left: Math.min(topRect.left, bottomRect.left),
    right: Math.max(topRect.right, bottomRect.right),
    top: topRect.top,
    bottom: bottomRect.bottom,
  };
  return rectHasArea(intersectRects(stageBand, hostView));
};

const getDragBarHeight = (toolbar) => {
  const dragBar = toolbar?.querySelector('.draw-floating-toolbar-header');
  return dragBar?.offsetHeight || DEFAULT_DRAG_BAR_HEIGHT;
};

const getToolbarHeight = (toolbar) => {
  const measured = toolbar?.offsetHeight || 0;
  return measured > 0 ? measured : getDragBarHeight(toolbar);
};

/**
 * Keep the menu inside [bounds.top, bounds.bottom].
 * Default: when the menu is taller than the band, top clamping wins.
 * preferBottom: when top anchor is off-screen and bottom is in view, keep the
 * menu bottom on bounds.bottom so it never goes past the bottom anchor.
 */
const clampToolbarTopToBand = (top, boundsTop, boundsBottom, toolbarHeight, preferBottom = false) => {
  const minTop = boundsTop;
  const maxTop = boundsBottom - toolbarHeight;
  if (maxTop < minTop) {
    return Math.round(preferBottom ? maxTop : minTop);
  }
  return clampPx(top, minTop, maxTop);
};

/** Top anchor fully above the visible host viewport. */
const isTopAnchorOutOfView = (lessonElementId) => {
  const { topAnchor } = getFloatEls(lessonElementId);
  if (!topAnchor) {
    return false;
  }
  const hostView = getHostViewRect(topAnchor);
  const topRect = getClientRect(topAnchor);
  return topRect.bottom <= hostView.top + VISIBLE_EPS_PX;
};

/** Bottom anchor intersects the visible host viewport. */
const isBottomAnchorInView = (lessonElementId) => {
  const { bottomAnchor } = getFloatEls(lessonElementId);
  if (!bottomAnchor) {
    return false;
  }
  const hostView = getHostViewRect(bottomAnchor);
  const bottomRect = getClientRect(bottomAnchor);
  return bottomRect.bottom > hostView.top + VISIBLE_EPS_PX
    && bottomRect.top < hostView.bottom - VISIBLE_EPS_PX;
};

/** Bottom clamp wins while scrolling the top away and the bottom is still on screen. */
const shouldPreferBottomClamp = (lessonElementId) => (
  isTopAnchorOutOfView(lessonElementId) && isBottomAnchorInView(lessonElementId)
);

/** Keep stage min-height at least the tallest measured toolbar for this instance. */
const syncStageMinHeight = (lessonElementId, toolbar) => {
  const { stage } = getFloatEls(lessonElementId);
  if (!stage || !toolbar) {
    return;
  }
  const h = getToolbarHeight(toolbar);
  const prev = Number.parseFloat(stage.style.getPropertyValue('--draw-float-toolbar-max-height')) || 0;
  const next = Math.max(h, prev, TOOLBAR_MAX_HEIGHT_FALLBACK_PX);
  stage.style.setProperty('--draw-float-toolbar-max-height', `${next}px`);
};

/**
 * Activity / multipart: clamp only between top & bottom anchors in document space.
 * Toolbar stays absolute and scrolls with content (no viewport sticky).
 */
const clampToAnchorBand = (x, y, toolbar, topAnchor, lessonElementId) => {
  if (!toolbar || !topAnchor) {
    return { x, y };
  }

  const { stage, bottomAnchor } = getFloatEls(lessonElementId);
  const toolbarWidth = toolbar.offsetWidth || ONE_COLUMN_WIDTH;
  const toolbarHeight = getToolbarHeight(toolbar);
  const topRect = topAnchor.getBoundingClientRect();
  const bottomRect = bottomAnchor
    ? bottomAnchor.getBoundingClientRect()
    : { bottom: topRect.top + (stage?.offsetHeight || toolbarHeight) };
  const bandBottom = bottomRect.bottom - topRect.top;

  const stageWidth = stage?.offsetWidth || topAnchor.offsetWidth || 0;
  const minX = -toolbarWidth - DOCK_GAP_FROM_DRAW;
  const maxX = Math.max(minX, stageWidth - toolbarWidth);

  return {
    x: clampPx(x, minX, maxX),
    y: clampToolbarTopToBand(y, 0, bandBottom, toolbarHeight, false),
  };
};

/** Sticky (standalone): clamp anchor-relative x/y between top/bottom anchors ∩ main. */
const clampToStickyBounds = (x, y, toolbar, topAnchor, lessonElementId) => {
  if (!toolbar || !topAnchor) {
    return { x, y };
  }

  const bounds = getClampBoundsRect(lessonElementId, topAnchor);
  const anchorRect = topAnchor.getBoundingClientRect();
  const toolbarWidth = toolbar.offsetWidth || ONE_COLUMN_WIDTH;
  const toolbarHeight = getToolbarHeight(toolbar);
  const preferBottom = shouldPreferBottomClamp(lessonElementId);

  const minX = bounds.left - anchorRect.left;
  const maxX = bounds.right - anchorRect.left - toolbarWidth;
  const minY = bounds.top - anchorRect.top;

  return {
    x: clampPx(x, minX, Math.max(minX, maxX)),
    y: clampToolbarTopToBand(
      y,
      minY,
      bounds.bottom - anchorRect.top,
      toolbarHeight,
      preferBottom,
    ),
  };
};

const clampToolbarPosition = (x, y, toolbar, topAnchor, lessonElementId, stickyScroll) => (
  stickyScroll
    ? clampToStickyBounds(x, y, toolbar, topAnchor, lessonElementId)
    : clampToAnchorBand(x, y, toolbar, topAnchor, lessonElementId)
);

/**
 * Start beside the draw area (top anchor = draw width/left/top).
 * Top stays on the top anchor (y = 0) when it fits.
 */
const computeStartPosition = (toolbar, topAnchor, lessonElementId, stickyScroll) => {
  const toolbarWidth = toolbar.offsetWidth || ONE_COLUMN_WIDTH;
  const x = -toolbarWidth - DOCK_GAP_FROM_DRAW;
  return clampToolbarPosition(x, 0, toolbar, topAnchor, lessonElementId, stickyScroll);
};

const layoutIsReady = (topAnchor, lessonElementId) => {
  const { stage, bottomAnchor } = getFloatEls(lessonElementId);
  if (!topAnchor || !stage || !bottomAnchor) {
    return false;
  }
  const stageRect = stage.getBoundingClientRect();
  const anchorRect = topAnchor.getBoundingClientRect();
  return stageRect.width > 0 && anchorRect.width > 0;
};

/**
 * Fixed pin: top anchor / main top pushes the menu down when scrolling up;
 * bottom anchor / main bottom pushes the menu up when scrolling down.
 * If the top anchor is out of view and the bottom is still in view, bottom
 * clamping wins so the menu bottom never goes past the bottom anchor.
 */
const computePinStyle = (toolbar, topAnchor, position, wasPinned, lessonElementId) => {
  if (!toolbar || !topAnchor || !position) {
    return null;
  }

  if (!isDrawStageInView(lessonElementId)) {
    return null;
  }

  const bounds = getClampBoundsRect(lessonElementId, topAnchor);
  const anchorRect = topAnchor.getBoundingClientRect();
  const toolbarHeight = getToolbarHeight(toolbar);
  const toolbarWidth = toolbar.offsetWidth || ONE_COLUMN_WIDTH;
  const pinOffset = bounds.top;
  const preferBottom = shouldPreferBottomClamp(lessonElementId);

  const naturalLeft = anchorRect.left + position.x;
  const naturalTop = anchorRect.top + position.y;
  const naturalBottom = naturalTop + toolbarHeight;

  const shouldPin = wasPinned
    ? (naturalTop < pinOffset + PIN_EXIT_HYSTERESIS_PX
      || naturalBottom > bounds.bottom - PIN_EXIT_HYSTERESIS_PX)
    : (naturalTop < pinOffset - PIN_ENTER_HYSTERESIS_PX
      || naturalBottom > bounds.bottom + PIN_ENTER_HYSTERESIS_PX);

  if (!shouldPin) {
    return null;
  }

  if (bounds.bottom - bounds.top <= VISIBLE_EPS_PX) {
    return null;
  }

  // Prefer bottom ride-out when the top anchor has scrolled away.
  const desiredTop = preferBottom
    ? Math.min(Math.max(naturalTop, pinOffset), bounds.bottom - toolbarHeight)
    : Math.max(naturalTop, pinOffset);

  const stickyTop = clampToolbarTopToBand(
    desiredTop,
    bounds.top,
    bounds.bottom,
    toolbarHeight,
    preferBottom,
  );

  const pinnedLeft = clampPx(
    naturalLeft,
    bounds.left,
    Math.max(bounds.left, bounds.right - toolbarWidth),
  );

  return {
    position: 'fixed',
    left: pinnedLeft,
    top: Math.round(stickyTop),
  };
};

/**
 * Draw toolbar chrome (collapse / resize / drag).
 *
 * Standalone (stickyScroll): position is top-anchor-relative; scroll pin uses
 * position:fixed within top-anchor … bottom-anchor ∩ visible <main>.
 * Activity / multipart (!stickyScroll): absolute between anchors only; scrolls
 * with the question content (no on-screen sticky).
 */
const useDrawFloatingToolbar = (lessonElementId, { stickyScroll = true } = {}) => {
  const toolbarRef = useRef(null);
  const dragState = useRef(null);
  const startInitializedRef = useRef(false);
  const hasDraggedRef = useRef(false);
  const positionRef = useRef(null);

  const saved = drawManager.getToolbarUi(lessonElementId);

  const [position, setPosition] = useState(null);
  const [pinStyle, setPinStyle] = useState(null);
  const [width, setWidth] = useState(() => normalizeExpandedWidth(saved?.width || ONE_COLUMN_WIDTH));
  const [collapsed, setCollapsed] = useState(() => !!saved?.collapsed);
  /** List/multipart sticky: false when this question's draw surface is off-screen. */
  const [isDrawInView, setIsDrawInView] = useState(true);

  const isTwoColumn = width >= TWO_COLUMN_MIN_WIDTH;
  const columns = isTwoColumn ? 2 : 1;
  const effectiveWidth = getModeWidth(isTwoColumn, collapsed);
  const isPlaced = position != null;
  const isPinned = stickyScroll && pinStyle != null;

  const pinStyleRef = useRef(null);

  positionRef.current = position;
  pinStyleRef.current = pinStyle;

  const persist = useCallback((next) => {
    drawManager.setToolbarUi(lessonElementId, next);
  }, [lessonElementId]);

  const applyPin = useCallback(() => {
    const toolbar = toolbarRef.current;
    const anchor = toolbar?.parentElement;
    const pos = positionRef.current;
    if (!toolbar || !anchor || !pos) {
      setPinStyle(null);
      setIsDrawInView(true);
      return;
    }

    syncStageMinHeight(lessonElementId, toolbar);

    // Activity / multipart: no sticky pin; toolbar scrolls with content.
    if (!stickyScroll) {
      setPinStyle(null);
      setIsDrawInView(true);
      return;
    }

    const inView = isDrawStageInView(lessonElementId);
    setIsDrawInView(inView);
    if (!inView) {
      setPinStyle(null);
      return;
    }

    const wasPinned = pinStyleRef.current != null;
    const next = computePinStyle(toolbar, anchor, pos, wasPinned, lessonElementId);
    setPinStyle((prev) => {
      if (!prev && !next) {
        return prev;
      }
      // CHATTER: ignore sub-pixel / 1px noise at the pin edge.
      if (prev && next
        && Math.abs(prev.left - next.left) < STYLE_DEAD_ZONE_PX
        && Math.abs(prev.top - next.top) < STYLE_DEAD_ZONE_PX) {
        return prev;
      }
      if (prev && next
        && prev.left === next.left
        && prev.top === next.top) {
        return prev;
      }
      return next;
    });
  }, [lessonElementId, stickyScroll]);

  /**
   * CHATTER-safe bottom/side clamp: only update stored position when it is
   * clearly outside the allowed rect (eps), so we do not fight pin at the edge.
   * Skip while pinned — pin style owns the bottom ride-out.
   * Skip on scroll for activity mode — absolute coords stay valid as content moves.
   */
  const softReclampIfOutside = useCallback(() => {
    if (!stickyScroll || dragState.current || pinStyleRef.current) {
      return;
    }
    const toolbar = toolbarRef.current;
    const anchor = toolbar?.parentElement;
    const pos = positionRef.current;
    if (!toolbar || !anchor || !pos) {
      return;
    }
    const next = clampToolbarPosition(pos.x, pos.y, toolbar, anchor, lessonElementId, true);
    if (
      Math.abs(next.x - pos.x) > BOUNDS_RECLAMP_EPS_PX
      || Math.abs(next.y - pos.y) > BOUNDS_RECLAMP_EPS_PX
    ) {
      setPosition(next);
    }
  }, [lessonElementId, stickyScroll]);

  const prevLessonElementIdRef = useRef(lessonElementId);

  // After render/layout: place start left/top, then keep pin in sync with scroll.
  useLayoutEffect(() => {
    if (prevLessonElementIdRef.current !== lessonElementId) {
      prevLessonElementIdRef.current = lessonElementId;
      startInitializedRef.current = false;
      hasDraggedRef.current = false;
      setPosition(null);
      setPinStyle(null);
      setIsDrawInView(true);
    }

    const tryPlace = () => {
      const toolbar = toolbarRef.current;
      const anchor = toolbar?.parentElement;
      if (!toolbar || !anchor || !layoutIsReady(anchor, lessonElementId)) {
        return false;
      }

      syncStageMinHeight(lessonElementId, toolbar);

      const savedUi = drawManager.getToolbarUi(lessonElementId);
      let next;
      if (savedUi?.floating || hasDraggedRef.current) {
        const x = savedUi?.floating ? savedUi.x : (positionRef.current?.x ?? 0);
        const y = savedUi?.floating ? savedUi.y : (positionRef.current?.y ?? 0);
        next = clampToolbarPosition(x, y, toolbar, anchor, lessonElementId, stickyScroll);
      } else {
        next = computeStartPosition(toolbar, anchor, lessonElementId, stickyScroll);
      }

      startInitializedRef.current = true;
      setPosition((prev) => (
        prev && prev.x === next.x && prev.y === next.y ? prev : next
      ));
      return true;
    };

    const onLayoutChange = () => {
      const currentToolbar = toolbarRef.current;
      const currentAnchor = currentToolbar?.parentElement;
      if (!currentToolbar || !currentAnchor || !layoutIsReady(currentAnchor, lessonElementId)) {
        return;
      }

      syncStageMinHeight(lessonElementId, currentToolbar);

      const savedUi = drawManager.getToolbarUi(lessonElementId);

      if (!hasDraggedRef.current && !savedUi?.floating) {
        const docked = computeStartPosition(currentToolbar, currentAnchor, lessonElementId, stickyScroll);
        startInitializedRef.current = true;
        setPosition((prev) => (
          prev && prev.x === docked.x && prev.y === docked.y ? prev : docked
        ));
        applyPin();
        return;
      }

      if (!startInitializedRef.current) {
        tryPlace();
        applyPin();
        return;
      }

      if (stickyScroll) {
        softReclampIfOutside();
      } else {
        // Activity: re-clamp docked/dragged pos to the anchor band after resize only.
        const pos = positionRef.current;
        if (pos) {
          const next = clampToolbarPosition(
            pos.x,
            pos.y,
            currentToolbar,
            currentAnchor,
            lessonElementId,
            false,
          );
          if (next.x !== pos.x || next.y !== pos.y) {
            setPosition(next);
          }
        }
      }
      applyPin();
    };

    if (!startInitializedRef.current) {
      tryPlace();
    } else {
      onLayoutChange();
    }
    if (stickyScroll) {
      softReclampIfOutside();
    }
    applyPin();

    const anchor = toolbarRef.current?.parentElement;
    const { stage, bottomAnchor } = getFloatEls(lessonElementId);
    const mainEl = stickyScroll
      ? (anchor?.closest('main') || document.querySelector('main'))
      : null;

    let ticking = false;
    const onScroll = () => {
      if (!stickyScroll) {
        return;
      }
      if (ticking) {
        return;
      }
      ticking = true;
      window.requestAnimationFrame(() => {
        softReclampIfOutside();
        applyPin();
        ticking = false;
      });
    };

    const onResize = () => {
      if (ticking) {
        return;
      }
      ticking = true;
      window.requestAnimationFrame(() => {
        onLayoutChange();
        ticking = false;
      });
    };

    const observer = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(onResize)
      : null;
    if (stage) {
      observer?.observe(stage);
    }
    if (anchor) {
      observer?.observe(anchor);
    }
    if (bottomAnchor) {
      observer?.observe(bottomAnchor);
    }
    if (mainEl) {
      observer?.observe(mainEl);
    }

    if (stickyScroll) {
      window.addEventListener('scroll', onScroll, true);
    }
    window.addEventListener('resize', onResize);

    return () => {
      observer?.disconnect();
      if (stickyScroll) {
        window.removeEventListener('scroll', onScroll, true);
      }
      window.removeEventListener('resize', onResize);
    };
  }, [lessonElementId, effectiveWidth, collapsed, stickyScroll, applyPin, softReclampIfOutside]);

  // Re-apply pin whenever stored position changes (e.g. after drag).
  useLayoutEffect(() => {
    applyPin();
  }, [position, applyPin]);

  const onDragStart = useCallback((e) => {
    if (e.button !== 0 || !positionRef.current) {
      return;
    }
    e.preventDefault();

    const toolbar = toolbarRef.current;
    const anchor = toolbar?.parentElement;
    if (!toolbar || !anchor) {
      return;
    }

    // When pinned (fixed), convert current screen location back to anchor-relative.
    const anchorRect = anchor.getBoundingClientRect();
    const toolbarRect = toolbar.getBoundingClientRect();
    let originX = toolbarRect.left - anchorRect.left;
    let originY = toolbarRect.top - anchorRect.top;

    const clamped = clampToolbarPosition(originX, originY, toolbar, anchor, lessonElementId, stickyScroll);
    originX = clamped.x;
    originY = clamped.y;
    setPosition(clamped);
    setPinStyle(null);

    dragState.current = {
      startX: e.clientX,
      startY: e.clientY,
      originX,
      originY,
    };

    const onMove = (moveEvent) => {
      if (!dragState.current || !toolbarRef.current) {
        return;
      }
      const currentAnchor = toolbarRef.current.parentElement;
      const dx = moveEvent.clientX - dragState.current.startX;
      const dy = moveEvent.clientY - dragState.current.startY;
      const next = clampToolbarPosition(
        dragState.current.originX + dx,
        dragState.current.originY + dy,
        toolbarRef.current,
        currentAnchor,
        lessonElementId,
        stickyScroll,
      );
      setPosition(next);
    };

    const onUp = () => {
      dragState.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      hasDraggedRef.current = true;
      setPosition((prev) => {
        if (!prev) {
          return prev;
        }
        persist({
          floating: true,
          x: prev.x,
          y: prev.y,
          width,
          collapsed,
        });
        return prev;
      });
      // Pin reapplied via position effect (standalone only).
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [persist, width, collapsed, lessonElementId, stickyScroll]);

  const onResizeStart = useCallback((e) => {
    if (e.button !== 0) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidth = width;

    const applyWidth = (nextWidth) => {
      const normalized = normalizeExpandedWidth(nextWidth);
      setWidth(normalized);
      const toolbar = toolbarRef.current;
      const anchor = toolbar?.parentElement;
      const savedUi = drawManager.getToolbarUi(lessonElementId);
      const pos = positionRef.current;
      if (toolbar && anchor && pos) {
        requestAnimationFrame(() => {
          const t = toolbarRef.current;
          const a = t?.parentElement;
          const p = positionRef.current;
          if (!t || !a || !p) {
            return;
          }
          const nextPos = clampToolbarPosition(p.x, p.y, t, a, lessonElementId, stickyScroll);
          setPosition(nextPos);
          persist({
            floating: !!savedUi?.floating,
            x: nextPos.x,
            y: nextPos.y,
            width: normalized,
            collapsed,
          });
        });
      } else {
        persist({
          ...(savedUi || {}),
          width: normalized,
          collapsed,
        });
      }
    };

    const onUp = (upEvent) => {
      window.removeEventListener('mouseup', onUp);
      const dx = upEvent.clientX - startX;
      let nextWidth;
      if (Math.abs(dx) < RESIZE_TOGGLE_DRAG_PX) {
        nextWidth = startWidth >= TWO_COLUMN_MIN_WIDTH ? ONE_COLUMN_WIDTH : TWO_COLUMN_WIDTH;
      } else if (dx > 0) {
        nextWidth = TWO_COLUMN_WIDTH;
      } else {
        nextWidth = ONE_COLUMN_WIDTH;
      }
      applyWidth(nextWidth);
    };

    window.addEventListener('mouseup', onUp);
  }, [width, lessonElementId, persist, collapsed, stickyScroll]);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      const savedUi = drawManager.getToolbarUi(lessonElementId);
      const pos = positionRef.current;
      const normalized = normalizeExpandedWidth(width);
      if (savedUi?.floating && pos) {
        persist({
          floating: true,
          x: pos.x,
          y: pos.y,
          width: normalized,
          collapsed: next,
        });
      } else {
        persist({
          ...(savedUi || {}),
          width: normalized,
          collapsed: next,
        });
      }
      return next;
    });
  }, [lessonElementId, persist, width]);

  return {
    toolbarRef,
    isPlaced,
    isPinned,
    isDrawInView,
    position,
    pinStyle,
    effectiveWidth,
    columns,
    collapsed,
    toggleCollapsed,
    onDragStart,
    onResizeStart,
  };
};

export default useDrawFloatingToolbar;
